import { useState, useCallback, useRef } from 'react'
import { processDirectoryEntry, createFileList } from '@/utils/folderProcessor'

interface UseFileDropProps {
  onFilesDropped: (files: File[]) => void
  onFolderDropped?: (files: FileList) => void
  accept?: Record<string, string[]>
  disabled?: boolean
}

interface UseFileDropReturn {
  isDragActive: boolean
  isDragOver: boolean
  dragProps: {
    onDragEnter: (e: React.DragEvent) => void
    onDragLeave: (e: React.DragEvent) => void
    onDragOver: (e: React.DragEvent) => void
    onDrop: (e: React.DragEvent) => void
  }
}

export const useFileDrop = ({
  onFilesDropped,
  onFolderDropped,
  accept,
  disabled = false,
}: UseFileDropProps): UseFileDropReturn => {
  const [isDragActive, setIsDragActive] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounterRef = useRef(0)

  const isValidFile = useCallback(
    (file: File) => {
      if (!accept) return true

      const mimeType = file.type
      const extension = `.${file.name.split('.').pop()?.toLowerCase()}`

      return Object.entries(accept).some(([acceptedMimeType, extensions]) => {
        // Check MIME type
        if (mimeType.match(acceptedMimeType.replace('*', '.*'))) {
          return true
        }
        // Check extension
        return extensions.includes(extension)
      })
    },
    [accept]
  )

  const onDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (disabled) return

      dragCounterRef.current++

      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        // Check if this is an internal drag (files being moved within the app)
        const hasInternalData = e.dataTransfer.types.includes('application/json')

        // Only activate dropzone for external file drops, not internal drags
        if (!hasInternalData) {
          // Check if any items are directories (for folder drops)
          const hasDirectories = Array.from(e.dataTransfer.items).some((item) => {
            // Check if the item is a directory
            return item.kind === 'file' && (item as any).webkitGetAsEntry?.()?.isDirectory
          })

          setIsDragActive(true)
          setIsDragOver(true)
        }
      }
    },
    [disabled]
  )

  const onDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (disabled) return

      dragCounterRef.current--

      if (dragCounterRef.current === 0) {
        setIsDragActive(false)
        setIsDragOver(false)
      }
    },
    [disabled]
  )

  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      // Check if this is an internal drag
      const hasInternalData = e.dataTransfer.types.includes('application/json')

      if (hasInternalData) {
        // Don't interfere with internal drags - let them bubble to other handlers
        return
      }

      e.preventDefault()
      e.stopPropagation()

      if (disabled) return

      // Enable drop for external files only
      e.dataTransfer.dropEffect = 'copy'
    },
    [disabled]
  )

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      // Check if this is an internal drag - if so, don't interfere
      const hasInternalData = e.dataTransfer.types.includes('application/json')

      if (hasInternalData) {
        // Let internal drags be handled by other components
        return
      }

      e.preventDefault()
      e.stopPropagation()

      if (disabled) return

      setIsDragActive(false)
      setIsDragOver(false)
      dragCounterRef.current = 0

      // First try to use the modern DataTransferItemList API for better folder support
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        const items = Array.from(e.dataTransfer.items)

        // Check if any items are directories
        const hasDirectories = items.some((item) => {
          const entry = (item as any).webkitGetAsEntry?.()
          return entry?.isDirectory
        })

        if (hasDirectories && onFolderDropped) {
          // Process directories using the FileSystem API
          const allFiles: File[] = []

          for (const item of items) {
            const entry = (item as any).webkitGetAsEntry?.()
            if (entry) {
              const files = await processDirectoryEntry(entry)
              allFiles.push(...files)
            }
          }

          // Create a FileList-like object
          const fileList = createFileList(allFiles)

          onFolderDropped(fileList)
          return
        }
      }

      // Fallback to the traditional files API
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files)

        // Check if any file has webkitRelativePath (indicating folder drop from file input)
        const hasFolderStructure = files.some((file) => (file as any).webkitRelativePath)

        if (hasFolderStructure && onFolderDropped) {
          // Handle as folder drop
          onFolderDropped(e.dataTransfer.files)
        } else {
          // Handle as regular file drop
          const validFiles = files.filter(isValidFile)

          if (validFiles.length > 0) {
            onFilesDropped(validFiles)
          }
        }

        // Clear the drag data
        e.dataTransfer.clearData()
      }
    },
    [disabled, isValidFile, onFilesDropped, onFolderDropped]
  )

  return {
    isDragActive,
    isDragOver,
    dragProps: {
      onDragEnter,
      onDragLeave,
      onDragOver,
      onDrop,
    },
  }
}
